"use client";

import { Suspense, useCallback, useState } from "react";
import { FocusedInput, START_DATE } from "@datepicker-react/hooks";
import Link from "next/link";
import { usePathname } from "next/navigation";
import CloseIcon from "components/icons-svg/CloseIcon";
import NewCalendarIcon from "components/icons-svg/NewCalendarIcon";
import NewGuestIcon from "components/icons-svg/NewGuestIcon";
import SearchIcon from "components/icons-svg/SearchIcon";
import SearchDateInput from "components/pages/vacation-rentals/SearchDateInput";
import SearchGuestPopoverInVacationRental from "components/pages/vacation-rentals/SearchGuestPopoverInVacationRental";
import useTrackSearchClick from "components/searchBar/mobile-tablet-view/drawer-buttons/useTrackSearchClick";
import SearchBarHomePageInputNew from "components/searchBar/SearchBarHomePageInputNew";
import { useAppDispatch, useAppSelector } from "stores/hooks";
import { markSearch, selectSearch } from "stores/search/searchSlice";
import { CurrentSearchTabType } from "stores/ui/types";
import { markSearchFlow, selectUI } from "stores/ui/uiSlice";
import { formatDateRangeWithoutLeadingZero } from "utils/dates";
import { handleGuestValue } from "utils/helper";
import useBreakpoint from "utils/hooks/useBreakpoint";
import { isSpringBreakDealsRoute } from "./springBreakIntent";
export const HomePageDateInputModalHeader = ({
  handleDateReset,
  onClose
}: {
  handleDateReset?: () => void;
  onClose?: () => void;
}) => {
  return <div className='sticky top-0 z-[1] flex w-full items-center justify-between border-b border-b-[#ccd0d0] bg-offWhite pt-1 lg:hidden' data-sentry-component="HomePageDateInputModalHeader" data-sentry-source-file="SearchBoxLogicNew.tsx">
      <button aria-label='Reset Dates' className='btn btn-ghost mt-1 px-2 text-base font-semibold underline' onClick={handleDateReset}>
        Clear
      </button>
      <button aria-label='Note on minimum stay' onClick={() => {
      onClose?.();
    }} className='btn btn-ghost ml-auto px-2 text-base'>
        <CloseIcon data-sentry-element="CloseIcon" data-sentry-source-file="SearchBoxLogicNew.tsx" />
      </button>
    </div>;
};
export const HomePageDateInputModalFooter = () => {
  const dispatch = useAppDispatch();
  const {
    currentSearchTab
  } = useAppSelector(selectUI);
  const handleSkip = useCallback(() => {
    if (currentSearchTab === "dates") {
      dispatch(markSearch({
        startDate: null,
        endDate: null,
        page: 0
      }));
      dispatch(markSearchFlow({
        currentSearchTab: "guest"
      }));
    }
  }, [dispatch, currentSearchTab]);
  return <div className='flex w-full items-center justify-between p-4' data-sentry-component="HomePageDateInputModalFooter" data-sentry-source-file="SearchBoxLogicNew.tsx">
      <button aria-label='Skip Dates' onClick={handleSkip} className='btn btn-ghost mt-1 px-2 text-base font-semibold underline'>
        Skip
      </button>
      <button type='button' className='btn btn-primary h-10 w-[100px] text-base font-semibold' onClick={() => {
      dispatch(markSearchFlow({
        currentSearchTab: null
      }));
    }}>
        Next
      </button>
    </div>;
};
const SearchBoxLogicNew = ({
  scrolled,
  compact = false,
  onToggle
}: {
  scrolled?: boolean;
  compact?: boolean;
  onToggle: () => void;
}) => {
  const pathname = usePathname();
  const track = useTrackSearchClick(); // Custom hook to call Mixpanel event
  const search = useAppSelector(selectSearch);
  const dispatch = useAppDispatch();
  const {
    currentSearchTab
  } = useAppSelector(selectUI);
  const {
    isDesktop
  } = useBreakpoint();
  const [focusedInput, setFocusedInput] = useState<FocusedInput>(null);
  const isSpringBreakRoute = isSpringBreakDealsRoute(pathname);
  const searchHref = search.currentUpdatedURL && search.currentUpdatedURL !== "/" ? search.currentUpdatedURL : null;
  const springBreakPopoverLayerClass = isSpringBreakRoute ? "!z-[1300]" : "";
  const springBreakContainerLayerClass = isSpringBreakRoute ? "z-[1300]" : "";
  const isExpandedSearchAction = currentSearchTab !== null;
  const isCompactScrolled = Boolean(scrolled && compact);
  const dividerClassName = isCompactScrolled ? "my-1.5 h-5 w-[2px] bg-accent-500" : "my-2.5 h-7 w-[3px] bg-accent-500";
  const searchActionClassName = `btn flex items-center justify-center overflow-hidden rounded-full bg-accent-500 text-white lg:transition-all lg:duration-300 ${isExpandedSearchAction ? `${scrolled ? `w-[32px] min-w-[32px] ${isCompactScrolled ? "px-3 lg:w-auto" : "px-3.5 lg:w-auto"}` : "w-[52px] px-5 py-4 lg:w-[119px]"}` : `px-0 ${scrolled ? "w-[32px] min-w-[32px]" : "w-[52px]"}`} ${scrolled ? "h-8" : "h-[52px]"} `;
  const searchActionLabel = "Search";
  const onFocusChange = useCallback((focusInput: FocusedInput) => {
    setFocusedInput(focusInput);
  }, []);
  const handleSearchFlow = (newCurrentSearchTab: CurrentSearchTabType) => {
    dispatch(markSearchFlow({
      currentSearchTab: newCurrentSearchTab
    }));
  };
  const handleDateFocus = useCallback(() => {
    onFocusChange(START_DATE);
  }, [onFocusChange]);
  const handleClearDates = () => {
    dispatch(markSearch({
      startDate: null,
      endDate: null,
      noOfDays: 0
    }));
    onFocusChange(START_DATE);
  };
  return <div className='mx-auto flex w-full overflow-visible' data-sentry-component="SearchBoxLogicNew" data-sentry-source-file="SearchBoxLogicNew.tsx">
      <div className={`relative flex w-full justify-between overflow-visible ${scrolled ? "pr-[54px] " : ""}`}>
        <div className='w-full'>
          <SearchBarHomePageInputNew handleDateFocus={handleDateFocus} scrolled={scrolled} data-sentry-element="SearchBarHomePageInputNew" data-sentry-source-file="SearchBoxLogicNew.tsx" />
        </div>
        <div className={dividerClassName} />
        <div className='relative h-full w-full'>
          <div className={`relative h-0 overflow-visible ${springBreakContainerLayerClass}`}>
            {/* Add the date picker popover */}
            {isDesktop && <Suspense>
                <SearchDateInput onFocusChange={onFocusChange} focusedInput={focusedInput} beforeCalendar={HomePageDateInputModalHeader} DateInputModalFooter={<HomePageDateInputModalFooter />} popoverStyle={` translate-x-[-50%] w-[925px] max-w-[90vw] ${springBreakPopoverLayerClass} border px-3 py-2 !rounded-[20px] bg-white shadow-[0px_3px_6px_#61797c29] left-[50%] ${scrolled ? "!top-[64px] lg4:left-[76%]" : "!top-[72px]"}`} />
              </Suspense>}
          </div>
          {/* Calendar Icon */}
          <div className={`pointer-events-none absolute left-6 transition-all duration-300
             ${formatDateRangeWithoutLeadingZero(search?.startDate, search?.endDate) ? "top-3.5" : "top-1/2 -translate-y-1/2"} `}>
            <NewCalendarIcon className='mr-2 h-5 w-5 text-[#8D91A5]' data-sentry-element="NewCalendarIcon" data-sentry-source-file="SearchBoxLogicNew.tsx" />
          </div>

          {/* Floating Label */}
          <label htmlFor='start-date' className={`absolute left-[52px] font-semibold transition-all duration-300 ${formatDateRangeWithoutLeadingZero(search?.startDate, search?.endDate) ? "top-2.5 text-xs leading-3 text-Gray-700" : `top-[53%] -translate-y-1/2 !leading-4 text-brand-dark ${scrolled ? "text-sm" : "text-base"}`}`}>
            Dates
          </label>
          <input className={`google-autocomplete-places input max-w-auto h-full w-full !overflow-hidden text-ellipsis whitespace-normal border-0 bg-white pl-[52px] pr-6 font-semibold leading-[100%] text-brand-dark focus:outline-none focus-visible:shadow-none ${scrolled ? "text-sm" : "text-base"} ${formatDateRangeWithoutLeadingZero(search?.startDate, search?.endDate) ? "pt-3.5" : ""}`} placeholder='' type='text' name='start-date' id='start-date' aria-describedby='start date' autoComplete='off' readOnly value={formatDateRangeWithoutLeadingZero(search?.startDate, search?.endDate)} onFocus={() => {
          onFocusChange(START_DATE);
          if (isDesktop) {
            handleSearchFlow("dates");
          }
        }} onClick={() => {
          onFocusChange(START_DATE);
          handleSearchFlow("dates");
          onToggle();
        }} />
          {search?.startDate !== "null" && search?.startDate !== null && <div className='absolute bottom-0 right-2.5 flex h-full items-center text-secondary-500'>
              <button className='btn h-auto min-w-0 p-1' onClick={handleClearDates}>
                <CloseIcon className='h-5 w-5' />
              </button>
            </div>}
        </div>

        <div className={dividerClassName} />

        <div className='w-full'>
          <SearchGuestPopoverInVacationRental initalState={{
          adultCount: search.adultCount || 0,
          childCount: search.childrenCount || 0,
          petsAllowed: search.petAllowed || false
        }} popoverStyle={`${scrolled ? "!top-[64px]" : "!top-[72px]"} ${springBreakPopoverLayerClass} mxd:left-[32px] !w-[350px] !max-w-[350px]`} onChange={res => {
          dispatch(markSearch({
            adultCount: res.adult,
            childrenCount: res.child,
            petAllowed: res.pets,
            numberOfGuest: res.adult + res.child,
            childAllowed: res.child > 0,
            page: 0
          }));
        }} data-sentry-element="SearchGuestPopoverInVacationRental" data-sentry-source-file="SearchBoxLogicNew.tsx">
            {currentState => <div className='relative h-full w-full'>
                {/* Icon */}
                <div className={`pointer-events-none absolute left-6 transition-all duration-200 
                  ${handleGuestValue({
              adultCount: currentState.adult,
              childCount: currentState.child,
              petsAllowed: currentState.pets
            }) || currentSearchTab === "guest" ? "top-3.5" : "top-1/2 -translate-y-1/2 "}`}>
                  <NewGuestIcon className='mr-2 h-5 w-6' />
                </div>

                {/* Floating Label */}
                <label id='guest-label' htmlFor='guest-count' className={`absolute left-[54px] font-semibold transition-all duration-200 ${handleGuestValue({
              adultCount: currentState.adult,
              childCount: currentState.child,
              petsAllowed: currentState.pets
            }) || currentSearchTab === "guest" ? "top-2.5 mb-1 text-xs leading-3 text-Gray-700" : `top-1/2 -translate-y-1/2 !leading-4 text-brand-dark  ${scrolled ? "text-sm" : "text-base"}`}`}>
                  Guests
                </label>

                {/* Input */}
                <input id='guest-count' name='guest-count' className={`google-autocomplete-places input xll2:max-w-auto h-full w-full max-w-[240px] truncate text-ellipsis border-0 bg-white pl-[55px] pr-6 font-semibold leading-[100%] text-brand-dark focus:outline-none focus-visible:shadow-none lg:max-w-[120px] lg2:max-w-[125px] min-[1105px]:max-w-[160px] lg4:max-w-[145px] min-[1250px]:max-w-[160px] min-[1390px]:max-w-[200px] xll2:max-w-[210px]
      ${scrolled ? "text-sm" : "text-base"} ${handleGuestValue({
              adultCount: currentState.adult,
              childCount: currentState.child,
              petsAllowed: currentState.pets
            }) || currentSearchTab === "guest" ? "pt-3.5" : ""}`} aria-label='guest count' placeholder='' value={handleGuestValue({
              adultCount: currentState.adult,
              childCount: currentState.child,
              petsAllowed: currentState.pets
            })} readOnly onClick={() => {
              handleSearchFlow("guest");
            }} />
                {/* close button */}
                {currentSearchTab === "guest" && <div className={`absolute bottom-0 flex hidden h-full items-center text-secondary-500 lg:flex
                  ${scrolled ? "right-[50px] xl:right-[60px]" : "right-[125px] lg4:right-[143px] "}`}>
                    <button className='btn h-auto min-w-0 p-1' onClick={() => dispatch(markSearchFlow({
                currentSearchTab: null
              }))}>
                      <CloseIcon className='h-5 w-5' />
                    </button>
                  </div>}
              </div>}
          </SearchGuestPopoverInVacationRental>
        </div>
        <div className={`absolute top-1/2 z-20 -translate-y-1/2 ${scrolled ? "right-2" : "right-0"}`}>
          {searchHref ? <Link href={searchHref} className={searchActionClassName} aria-label='Search' onClick={() => {
          if (search.placeId) {
            void track(search); // Trigger Mixpanel event
          }
        }}>
              <>
                <span>
                  <SearchIcon className={`text-white ${isExpandedSearchAction ? `lg:search-button-rtl ${scrolled ? "" : "lg:mb-0.5"}` : ""} ${scrolled ? "h-[18px] w-3" : "h-[21px] w-3.5 "}`} />
                </span>
                <span className={`ml-2 hidden font-semibold leading-6 text-white transition-opacity duration-300 ${scrolled ? "text-sm" : "text-base"} ${isExpandedSearchAction ? "lg:search-button-rtl lg:flex " : ``}`}>
                  {searchActionLabel}
                </span>
              </>
            </Link> : <button className={searchActionClassName} aria-label='Search' onClick={() => {
          if (search.placeId) {
            void track(search); // Trigger Mixpanel event
          }
        }}>
              <span>
                <SearchIcon className={`text-white ${isExpandedSearchAction ? `lg:search-button-rtl ${scrolled ? "" : "lg:mb-0.5"}` : ""} ${scrolled ? "h-[18px] w-3" : "h-[21px] w-3.5 "}`} />
              </span>
              <span className={`ml-2 hidden font-semibold leading-6 text-white transition-opacity duration-300 ${scrolled ? "text-sm" : "text-base"} ${isExpandedSearchAction ? "lg:search-button-rtl lg:flex" : ""}`}>
                {searchActionLabel}
              </span>
            </button>}
        </div>
      </div>
    </div>;
};
export default SearchBoxLogicNew;